/**
 *  @class
 *  @function CstRewardsBar
 *
 *  Single-bar milestone rewards ladder (Variant B — docs/design/2026-08-07-rewards-bar-v2-single-bar.md).
 *  Replaces the removed spend-and-earn system.
 *
 *  Engine (unchanged since the Sol-hardened v1):
 *  - The theme replaces cart DOM FIRST, then fires `line-item:change:end` (app.js/cart.js), so a
 *    recreated instance paints instantly at the last known total (static state) and the event
 *    drives ONE forward transition. No rewind, no recreation heuristics.
 *  - Listeners live on `document`, cleaned up in disconnectedCallback; a static generation
 *    counter drops stale async responses; the "Rewards earned" attribute write is gated store-wide.
 *
 *  Paint layer (v2): one fill percentage over piecewise segments between evenly-spaced markers.
 *  Marker positions are SERVER-rendered by the snippet (single source of position math); JS reads
 *  them back from the DOM. Confetti bursts anchor to marker positions; unlocking the final tier
 *  runs a finale (staggered bursts + fill shimmer).
 */
if (!customElements.get('cst-rewards-bar')) {
  class CstRewardsBar extends HTMLElement {
    // Shared across instances and across innerHTML re-creation (cents, eligible total).
    static lastTotal = null;
    // Whether the cart holds any eligible merchandise LINE (a $0-discounted line still counts) —
    // the unlock predicate for zero-threshold tiers.
    static lastHasMerch = null;
    // Monotonic cart generation: async work tags itself and only the latest may apply.
    static cartGen = 0;
    // Class-level gate: one "Rewards earned" attribute write store-wide at a time.
    static noteBusy = false;
    static initialSyncDone = false;
    // Expander state survives section re-renders.
    static detailOpen = false;

    constructor() {
      super();
    }

    connectedCallback() {
      const configEl = this.querySelector('script[type="application/json"]');
      if (!configEl) return;
      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (e) {
        return;
      }
      this.tiers = this.config.tiers || [];
      this.tiersAsc = [...this.tiers].sort((a, b) => a.threshold - b.threshold);
      this.strings = this.config.strings || {};
      this.messageEl = this.querySelector('.cst-rewards-bar__message');
      this.trackEl = this.querySelector('.cst-track');
      this.fillEl = this.querySelector('.cst-fill');
      this.serverTotal = parseInt(this.dataset.total, 10) || 0;
      this.serverHasMerch = this.dataset.hasMerch === 'true';
      this.pts = this.buildPts();

      this.onLineChangeEnd = this.onLineChangeEnd.bind(this);
      this.onCartMutated = this.onCartMutated.bind(this);
      document.addEventListener('line-item:change:end', this.onLineChangeEnd);
      document.addEventListener('cart:item-added', this.onCartMutated);
      document.addEventListener('cart:refresh', this.onCartMutated);

      const viewBtn = this.querySelector('.cst-view');
      const panel = this.querySelector('.cst-panel');
      if (viewBtn && panel) {
        viewBtn.addEventListener('click', () => {
          CstRewardsBar.detailOpen = !CstRewardsBar.detailOpen;
          this.applyPanelState();
        });
      }

      // Paint the last known state instantly, then transition to the server-rendered truth.
      if (CstRewardsBar.lastHasMerch === null) CstRewardsBar.lastHasMerch = this.serverHasMerch;
      const prev = CstRewardsBar.lastTotal === null ? this.serverTotal : CstRewardsBar.lastTotal;
      this.paint(prev, false);
      if (prev !== this.serverTotal) {
        this._raf = requestAnimationFrame(() => {
          this._raf = requestAnimationFrame(() => this.update(this.serverTotal, this.serverHasMerch));
        });
      } else {
        CstRewardsBar.lastTotal = this.serverTotal;
      }

      // A qualifying cart loaded fresh (no mutation before checkout) must still get its
      // "Rewards earned" stamp. One store-wide initial sync.
      if (!CstRewardsBar.initialSyncDone && this.tiers.some((t) => t.kind === 'gift')) {
        CstRewardsBar.initialSyncDone = true;
        this.refreshFromServer(++CstRewardsBar.cartGen);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('line-item:change:end', this.onLineChangeEnd);
      document.removeEventListener('cart:item-added', this.onCartMutated);
      document.removeEventListener('cart:refresh', this.onCartMutated);
      if (this._raf) cancelAnimationFrame(this._raf);
    }

    // ── THEME BRIDGE — the only Distinctive-specific part. Porting to another theme means
    // re-pointing these two handlers at that theme's cart events. ──────────────────────────
    onLineChangeEnd(event) {
      const cart = event.detail && event.detail.cart;
      const gen = ++CstRewardsBar.cartGen;
      if (cart && Array.isArray(cart.items)) {
        this.applyCart(cart);
      } else {
        this.refreshFromServer(gen);
      }
    }

    onCartMutated() {
      this.refreshFromServer(++CstRewardsBar.cartGen);
    }
    // ──────────────────────────────────────────────────────────────────────────────────────

    refreshFromServer(gen) {
      fetch(`${window.theme.routes.cart_url}.js`)
        .then((response) => response.json())
        .then((cart) => {
          // Drop responses that lost the race to a newer cart state.
          if (gen !== undefined && gen !== CstRewardsBar.cartGen) return;
          this.applyCart(cart);
        })
        .catch(() => {});
    }

    eligibleItems(cart) {
      return cart.items.filter((item) => {
        const sku = (item.sku || '').toUpperCase();
        return !sku.includes('DESTASH') && !sku.includes('ADVENT');
      });
    }

    applyCart(cart) {
      const items = this.eligibleItems(cart);
      const total = items.reduce((sum, item) => sum + item.final_line_price, 0);
      // Rewards are physical: eligibility needs a SHIPPED line (printables are digital).
      // Printable spend still counts toward thresholds — checkout's shipping condition does too.
      const hasMerch = items.some((item) => item.requires_shipping);
      this.update(total, hasMerch);
      this.syncRewardsNote(cart, total, hasMerch);
    }

    /**
     * Stamp earned gift rewards onto the cart as the "Rewards earned" attribute. Cart attributes
     * carry onto the order (note_attributes), so admin and the packing slip show exactly what was
     * promised at purchase time — the fulfilment model chosen 2026-08-08: gifts are packed from
     * SEPARATE stock and never added to the cart (plan.yml). Silent mutation: no cart:refresh
     * (nothing visible changes) and diffed against the current attribute so it cannot loop.
     *
     * NO gift tiers (post-sale: settings.rewards_bar_gifts_enable == false) means earned === '',
     * which CLEARS a stamp left on a cart that was built during the sale. Bailing out early here
     * was the 2026-08 leak: the attribute became write-only the moment the gifts were switched
     * off, so 17 post-sale orders carried a phantom "Rewards earned" into the admin (#36036 was
     * stamped 3.8 days after the window closed). Carts persist ~91 days and nothing server-side
     * can blank one — this is the only writer that can. Carts where the bar never RENDERS
     * (printable-only, unsupported country) are covered by the net in layout/theme.liquid.
     */
    syncRewardsNote(cart, total, hasMerch) {
      const gifts = this.tiers.filter((t) => t.kind === 'gift');
      const earned = gifts
        .filter((t) => this.unlockedAt(t, total, hasMerch))
        .map((t) => t.label)
        .join(', ');
      const current = (cart.attributes && cart.attributes['Rewards earned']) || '';
      if (current === earned || CstRewardsBar.noteBusy) return;
      CstRewardsBar.noteBusy = true;
      fetch(`${window.theme.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { 'Rewards earned': earned } })
      })
        .catch(() => {})
        .finally(() => {
          CstRewardsBar.noteBusy = false;
        });
    }

    // ── paint layer ────────────────────────────────────────────────────────────────────────

    // Piecewise points [(cents, pos%)] from the server-rendered markers — the snippet is the
    // single source of position math.
    buildPts() {
      const markers = [...this.querySelectorAll('.cst-mk')]
        .map((mk) => [parseInt(mk.dataset.threshold, 10), parseFloat(mk.style.left)])
        .sort((a, b) => a[0] - b[0]);
      return [[0, 0], ...markers];
    }

    fillPct(total) {
      const pts = this.pts;
      if (!pts.length || total <= 0) return 0;
      const last = pts[pts.length - 1];
      if (total >= last[0]) return 100;
      for (let i = pts.length - 1; i >= 1; i--) {
        const [loC, loP] = pts[i - 1];
        const [hiC, hiP] = pts[i];
        if (total >= loC && hiC > loC) {
          if (total < hiC) return loP + ((hiP - loP) * (total - loC)) / (hiC - loC);
        }
      }
      return 0;
    }

    // Pure unlock predicate: zero-threshold tiers unlock on merchandise PRESENCE (a line
    // discounted to $0 still counts); money tiers unlock on the eligible total.
    unlockedAt(tier, total, hasMerch) {
      if (tier.threshold === 0) return !!hasMerch;
      return total >= tier.threshold;
    }

    isUnlocked(tier, total) {
      return this.unlockedAt(tier, total, CstRewardsBar.lastHasMerch);
    }

    update(total, hasMerch) {
      const prevTotal = CstRewardsBar.lastTotal === null ? total : CstRewardsBar.lastTotal;
      const prevMerch = CstRewardsBar.lastHasMerch === null ? hasMerch : CstRewardsBar.lastHasMerch;
      CstRewardsBar.lastTotal = total;
      CstRewardsBar.lastHasMerch = !!hasMerch;
      this.paint(total, true);

      const crossed = this.tiersAsc.filter(
        (t) => !this.unlockedAt(t, prevTotal, prevMerch) && this.unlockedAt(t, total, hasMerch)
      );
      if (!crossed.length) return;
      const allNow = this.tiersAsc.every((t) => this.unlockedAt(t, total, hasMerch));
      crossed.forEach((tier, i) => {
        const isLastCross = i === crossed.length - 1;
        if (allNow && isLastCross) return; // the finale carries the final tier's celebration
        this.celebrate(tier);
      });
      if (allNow) this.finale(crossed[crossed.length - 1]);
    }

    paint(total, animate) {
      if (!animate) this.classList.add('cst--no-anim');
      if (this.fillEl) this.fillEl.style.setProperty('--cst-fill', `${this.fillPct(total)}%`);
      const maxCents = this.pts.length ? this.pts[this.pts.length - 1][0] : 0;
      if (this.trackEl && maxCents > 0) {
        this.trackEl.setAttribute('aria-valuenow', String(Math.min(total, maxCents)));
        this.trackEl.setAttribute('aria-valuetext', `${this.formatMoneyPlain(total)} of ${this.formatMoneyPlain(maxCents)}`);
      }

      const nextLocked = this.tiersAsc.find((t) => !this.isUnlocked(t, total));
      this.querySelectorAll('.cst-mk').forEach((mk) => {
        const threshold = parseInt(mk.dataset.threshold, 10);
        const unlocked = total > 0 && total >= threshold;
        mk.classList.toggle('cst-mk--on', unlocked);
        const isNext = nextLocked && parseInt(mk.dataset.threshold, 10) === nextLocked.threshold && mk.dataset.tierId === nextLocked.id;
        mk.classList.toggle('cst-mk--near', !unlocked && !!isNext && threshold > 0 && total / threshold >= 0.85);
      });
      this.querySelectorAll('.cst-row').forEach((row) => {
        const threshold = parseInt(row.dataset.threshold, 10);
        row.classList.toggle('cst-row--unlocked', total > 0 && total >= threshold);
      });

      // Condensed "everything earned" layout. Driven off the same nextLocked probe the message
      // uses, so the class and the copy can never disagree. total > 0 keeps an empty cart in the
      // full layout (it is motivation, not a completed goal).
      this.classList.toggle('cst--complete', !nextLocked && total > 0);

      this.applyPanelState();
      this.renderMessage(total);
      if (!animate) {
        void this.offsetWidth;
        this.classList.remove('cst--no-anim');
      }
    }

    // Collapsed content must leave the focus order and the accessibility tree (a 0fr grid row
    // clips visually but its links stay tabbable without inert/aria-hidden).
    applyPanelState() {
      const panel = this.querySelector('.cst-panel');
      const viewBtn = this.querySelector('.cst-view');
      if (!panel || !viewBtn) return;
      const open = CstRewardsBar.detailOpen;
      panel.classList.toggle('cst-panel--open', open);
      panel.toggleAttribute('inert', !open);
      if (open) {
        panel.removeAttribute('aria-hidden');
      } else {
        panel.setAttribute('aria-hidden', 'true');
      }
      viewBtn.setAttribute('aria-expanded', String(open));
    }

    formatMoneyPlain(cents) {
      const formatted = window.formatMoney
        ? window.formatMoney(cents, window.theme.settings.money_with_currency_format || '${{amount}}')
        : `$${(cents / 100).toFixed(2)}`;
      return formatted.replace(/<[^>]*>/g, '');
    }

    renderMessage(total) {
      if (!this.messageEl) return;
      const nextTier = this.tiersAsc.find((t) => !this.isUnlocked(t, total));
      if (!nextTier) {
        const shippingOnly = this.tiers.length === 1 && this.tiers[0].kind === 'shipping';
        this.messageEl.innerHTML = (shippingOnly ? this.strings.appliedAtCheckout : this.strings.allUnlocked) || '';
        return;
      }
      const remaining = Math.max(nextTier.threshold, 1) - total;
      const money = window.formatMoney
        ? window.formatMoney(remaining, window.theme.settings.money_with_currency_format || '${{amount}}')
        : `$${(remaining / 100).toFixed(2)}`;
      const template = nextTier.kind === 'shipping' && this.strings.spendMoreShippingHtml
        ? this.strings.spendMoreShippingHtml
        : this.strings.spendMoreHtml || '';
      // Locale strings carry their own <span> around the amount placeholder.
      this.messageEl.innerHTML = template
        .replace('%%AMOUNT%%', money)
        .replace('%%REWARD%%', nextTier.label);
    }

    markerFor(tier) {
      return this.querySelector(`.cst-mk[data-tier-id="${tier.id}"]`);
    }

    celebrate(tier) {
      const mk = this.markerFor(tier);
      if (mk) {
        mk.classList.add('cst-mk--pop');
        setTimeout(() => mk.classList.remove('cst-mk--pop'), 450);
        this.spawnConfetti(mk.style.left, 14);
      }
      this.announce(`${tier.label} — ${this.strings.unlocked || 'unlocked'}`);
    }

    // Unlocking the whole ladder: staggered bursts along the bar, a bigger final burst, and a
    // shimmer sweep on the fill.
    finale(finalTier) {
      const markers = [...this.querySelectorAll('.cst-mk')];
      markers.forEach((mk, i) => {
        setTimeout(() => {
          mk.classList.add('cst-mk--pop');
          setTimeout(() => mk.classList.remove('cst-mk--pop'), 450);
          this.spawnConfetti(mk.style.left, mk.dataset.tierId === finalTier.id ? 24 : 8);
        }, i * 110);
      });
      if (this.fillEl) {
        this.fillEl.classList.add('cst-finale');
        setTimeout(() => this.fillEl && this.fillEl.classList.remove('cst-finale'), 1200);
      }
      this.announce(this.strings.allUnlocked || '');
    }

    spawnConfetti(leftPct, count) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (!this.trackEl) return;
      const burst = document.createElement('div');
      burst.className = 'cst-confetti';
      burst.style.left = leftPct;
      const colors = ['#d4af37', '#936347', '#f0e0b8', '#fff'];
      for (let i = 0; i < count; i++) {
        const p = document.createElement('span');
        const angle = -Math.PI * (0.1 + 0.8 * Math.random());
        const dist = 26 + Math.random() * 48;
        p.style.setProperty('--cst-dx', `${Math.cos(angle) * dist}px`);
        p.style.setProperty('--cst-dy', `${Math.sin(angle) * dist}px`);
        p.style.setProperty('--cst-rot', `${Math.round(Math.random() * 540 - 270)}deg`);
        p.style.background = colors[i % colors.length];
        p.style.animationDelay = `${Math.random() * 90}ms`;
        burst.appendChild(p);
      }
      this.trackEl.appendChild(burst);
      setTimeout(() => burst.remove(), 1100);
    }

    // Persistent live region OUTSIDE the re-rendered subtree so announcements survive re-renders.
    announce(text) {
      let live = document.getElementById('cst-rewards-live');
      if (!live) {
        live = document.createElement('div');
        live.id = 'cst-rewards-live';
        live.className = 'visually-hidden';
        live.setAttribute('aria-live', 'polite');
        document.body.appendChild(live);
      }
      live.textContent = text;
    }
  }

  customElements.define('cst-rewards-bar', CstRewardsBar);
}
